import { UnsafeJobHtmlRenderer } from '@seek/job-html-renderer';
import { useTranslations } from '@vocab/react';
import { Inline, Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  useRef,
  type Ref,
  type RefObject,
  useContext,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SECTION_JOB_DESCRIPTION } from 'src/app/shared/constants/pageSectionName';
import { NotProvidedText } from 'src/app/v1/views/components/NotProvidedText';
import { StandardWritingFlow } from 'src/app/v1/views/components/StandardWritingFlow';
import { Video } from 'src/app/v1/views/components/Video';
import { VideoPreview } from 'src/app/v1/views/components/Video/components/VideoPreview';
import { EditFlowDataContext } from 'src/app/v1/views/context/cajaFlowData';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/v1/views/shared/ContentLoader';
import type { AppState } from 'src/types/appState';
import type { VideoRef } from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

const Read = () => {
  const { jobDescription, videoUrl } = useSelector(
    (state: AppState) => ({
      jobDescription: state.formData.jobDescription,
      videoUrl: state.formData.videoUrl,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  return (
    <Stack space="large">
      <ContentLoader loading={loading} lines={['85%', '85%', '50%', '85%']}>
        <UnsafeJobHtmlRenderer>{jobDescription}</UnsafeJobHtmlRenderer>
      </ContentLoader>
      <Stack space="medium">
        <Inline space="xsmall" alignY="center">
          <Text weight="strong">
            {t('job-description-section-read-video-label')}
          </Text>
          <Text tone="secondary">
            {t('job-description-section-read-video-secondary-label')}
          </Text>
        </Inline>
        <ContentLoader loading={loading} height={120} lines={[160]}>
          {videoUrl.length > 0 ? (
            <VideoPreview
              videoUrl={videoUrl}
              handleReset={() => {}}
              hideRemoveVideoButton
            />
          ) : (
            <NotProvidedText />
          )}
        </ContentLoader>
      </Stack>
    </Stack>
  );
};

interface EditProps {
  refs: {
    jobDescriptionRef: RefObject<any>;
    videoRef: RefObject<VideoRef>;
  };
}

const Edit = ({ refs }: EditProps) => (
  <Stack space="medium">
    <StandardWritingFlow ref={refs.jobDescriptionRef} />
    <Video ref={refs.videoRef} />
  </Stack>
);

export const JobDescriptionSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const jobDescriptionRef = useRef<any>();
    const videoRef = useRef<VideoRef>(null);

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('job-description-section-title')}
        name={SECTION_JOB_DESCRIPTION}
        ref={ref}
        fields={[jobDescriptionRef, videoRef]}
        read={<Read />}
        edit={<Edit refs={{ jobDescriptionRef, videoRef }} />}
      />
    );
  },
);
